// The Build bar and the tab bar (IMPLEMENTATION §2.6 "Chrome contents" / "Hit targets", §2.7 Mode FSM, UX §2).
//   Build bar  replaces the counter in Build (same rect): Move · Chute (from u10) · Sell (a tool AND a drop
//              zone: 100% refund) · Undo n (the build-undo depth), each ≥ hit.buildToolMinW; the armed tool
//              wears the brass plate. A tap on Sell while something is selected sells it (tapZone).
//   Tab bar    Shop · Build · Settings in three fixed slots (≥ hit.tabMinW), hidden until the first sale
//              (§2.7 "the tabs slide in at the first sale": u04 unlocks the Shop and Build; the row stays
//              reserved); Build reads "Done" in Build; a dot marks Shop cards revealed since the Shop was
//              last opened. Inert while a drag runs.
import { BUILD_BAR, type BuildBarSlot } from '../../input/dropZones.ts';
import { hasFeature } from '../../sim/query.ts';
import { fillCopy } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import { chipShown } from '../intro.ts';
import {
  draggingSig,
  freshSig,
  hoverZoneSig,
  introSig,
  layoutSig,
  modeSig,
  panelSig,
} from '../state.ts';
import { snapshotSig } from '../store.ts';
import { box } from './box.ts';

export function BuildBar() {
  const l = layoutSig.value;
  const m = modeSig.value;
  if (!l || !m || m.mode !== 'build') return null;
  const ctx = shellCtx();
  const copy = ctx.data().copy;
  const hot = hoverZoneSig.value;
  const label = (slot: BuildBarSlot): string =>
    slot === 'undo'
      ? fillCopy(copy.build.undo ?? 'Undo {n}', { n: m.undo })
      : (copy.build[slot] ?? slot);
  const toolMinW = `${ctx.data().ui.hit.buildToolMinW}px`;
  const onTool = (slot: BuildBarSlot): void => {
    ctx.actions.click();
    if (slot === 'undo') ctx.actions.undoBuild();
    else if (slot === 'sell' && ctx.actions.selected()) ctx.actions.tapZone('sell');
    else ctx.actions.setTool(slot);
  };
  return (
    <div class="build-bar" style={box(l.counter)} data-testid="buildbar">
      {BUILD_BAR.map((slot) => {
        const on = slot !== 'undo' && m.tool === slot;
        const disabled = (slot === 'chute' && !m.canChute) || (slot === 'undo' && m.undo === 0);
        return (
          <button
            key={slot}
            type="button"
            class={`tool ctl tool-${slot} ${on ? 'on' : ''} ${slot === 'sell' && hot === 'sell' ? 'hot' : ''}`}
            data-tool={slot}
            data-hit={`tool-${slot}`}
            data-zone={slot === 'sell' ? 'sell' : undefined}
            aria-pressed={slot === 'undo' ? undefined : on}
            style={{ minWidth: toolMinW }}
            disabled={disabled}
            onClick={() => onTool(slot)}
          >
            <span class="tool-label">{label(slot)}</span>
          </button>
        );
      })}
    </div>
  );
}

export function TabBar() {
  const l = layoutSig.value;
  void snapshotSig.value; // the Shop / Build unlocks follow the sim at the snapshot rate
  if (!l) return null;
  const ctx = shellCtx();
  const copy = ctx.data().copy;
  const s = ctx.state();
  const d = ctx.data().sim;
  const m = modeSig.value;
  const panel = panelSig.value.k;
  const shown = chipShown(introSig.value.phase) && hasFeature(s, d, 'shop');
  const shop = shown;
  const build = shown && (m?.canBuild ?? false);
  const inBuild = m?.mode === 'build';
  const dot = freshSig.value.length > 0 && panel !== 'shop';
  const tabW = { minWidth: `${ctx.data().ui.hit.tabMinW}px` };
  return (
    <nav
      class={`tabbar ${shown ? '' : 'hidden'} ${draggingSig.value ? 'inert' : ''}`}
      style={box(l.tabs)}
      data-testid="tabbar"
      aria-hidden={!shown}
    >
      {shop ? (
        <button
          type="button"
          class={`tab ctl ${panel === 'shop' ? 'on' : ''}`}
          data-tab="shop"
          style={tabW}
          data-hit="tab-shop"
          data-fx="shopTab"
          onClick={() => {
            ctx.actions.click();
            ctx.shell.togglePanel('shop');
          }}
        >
          <span>{copy.tabs.shop ?? 'Shop'}</span>
          {dot ? <i class="tab-dot" data-testid="shop-dot" /> : null}
        </button>
      ) : (
        <span class="tab-slot" style={tabW} />
      )}
      {build ? (
        <button
          type="button"
          class={`tab ctl ${inBuild ? 'on' : ''}`}
          data-tab="build"
          style={tabW}
          data-hit="tab-build"
          onClick={() => {
            ctx.actions.click();
            ctx.actions.toggleBuild();
          }}
        >
          <span>{inBuild ? (copy.tabs.done ?? 'Done') : (copy.tabs.build ?? 'Build')}</span>
        </button>
      ) : (
        <span class="tab-slot" style={tabW} />
      )}
      {shown ? (
        <button
          type="button"
          class={`tab ctl ${panel === 'settings' ? 'on' : ''}`}
          data-tab="settings"
          style={tabW}
          data-hit="tab-settings"
          onClick={() => {
            ctx.actions.click();
            ctx.shell.togglePanel('settings');
          }}
        >
          <span>{copy.tabs.settings ?? 'Settings'}</span>
        </button>
      ) : (
        <span class="tab-slot" style={tabW} />
      )}
    </nav>
  );
}
